<template>
  <div>
    <p class="cm-mg-p-wzd">1. {{ $t('common_words.general_information') }}</p>
    <hr />
    <div class="row">
      <div class="col-12 col-md-6">
        <div class="form-group has-label">
          <label>{{ $t('common_words.company') }}: *</label>
          <el-select
            clearable
            filterable
            class="select-primary custom-width-select required-business shadow-sm"
            size="large"
            v-model="model.business_id"
            :placeholder="`${$t('common_words.select')}...`"
            @change="model.farm_id = null"
          >
            <el-option
              class="select-primary"
              v-for="business in businesses"
              :key="business.id"
              :value="business.id"
              :label="business.name"
            />
          </el-select>
        </div>
      </div>
      <div class="col-12 col-md-6">
        <div class="form-group has-label">
          <label>{{ $t('common_words.farm') }}: *</label>
          <el-select
            clearable
            filterable
            class="select-primary custom-width-select required-farm shadow-sm"
            size="large"
            v-model="model.farm_id"
            :placeholder="`${$t('common_words.select')}...`"
          >
            <el-option
              class="select-primary"
              v-for="farm in filteredFarms"
              :key="farm.id"
              :value="farm.id"
              :label="farm.name"
            />
          </el-select>
        </div>
      </div>
    </div>
    <p class="cm-mg-p-wzd text-center text-muted">
      {{ $t('common_words.beds_creation') }}
    </p>
    <hr />
    <div class="d-flex flex-nowrap overflow-auto pb-3">
      <div
        v-for="(_, index) in model.names"
        :key="`bed-${index}`"
        class="border rounded-sm shadow-sm p-3 mr-3 flex-shrink-0"
        style="width: 420px; background-color: #fcfffb !important"
      >
        <div class="row">
          <div class="col-4">
            <fg-input
              type="number"
              min="1"
              :label="`${$t('common_words.order')}: *`"
              v-model.number="model.orders[index]"
              :name="`common_words.order_${index}`"
              :error="getFieldErrorByIndex('common_words.order', true, index)"
              v-validate="modelValidations.orders"
            />
          </div>
          <div class="col-6">
            <fg-input
              type="text"
              :label="`${$t('common_words.name')}: *`"
              v-model="model.names[index]"
              :name="`'common_words.name_${index}`"
              :error="getFieldErrorByIndex('common_words.name', true, index)"
              v-validate="modelValidations.names"
            />
          </div>
          <div class="col-2 pt-4 text-center">
            <a
              v-tooltip.top-center="$t('common_words.add')"
              class="btn-success btn-simple btn-link custom-margin-actions"
              @click="addBed(index)"
            >
              <em class="fa fa-plus custom-size-fa-actions"></em>
            </a>
            <a
              v-if="model.names.length > 1"
              v-tooltip.top-center="$t('common_words.erase')"
              class="btn-danger btn-simple btn-link custom-margin-actions"
              @click="removeBed(index)"
            >
              <em class="fa fa-times custom-size-fa-actions"></em>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Axios } from 'src/class/Axios.js';
import { isset } from 'src/util/Util';
import MixinAlert from 'src/mixins/mixin-alert';
import MixinForm from 'src/mixins/forms/mixin-form';
import MixinLoading from 'src/mixins/mixin-loading';
import MixinPermissions from 'src/util/mixinPermissions';

export default {
  mixins: [MixinAlert, MixinForm, MixinLoading, MixinPermissions],
  data() {
    return {
      businesses: [],
      farms: [],
      model: {
        business_id: null,
        farm_id: null,
        ids: [null],
        orders: [1],
        names: [null],
      },
      modelValidations: {
        orders: {
          required: true,
          min_value: 1,
        },
        names: {
          required: true,
        },
      },
    };
  },
  computed: {
    filteredFarms() {
      if (!isset(this.model.business_id)) return this.farms;
      return this.farms.filter((farm) => {
        if (farm.business_id == this.model.business_id) return true;
        const businesses = farm.businesses || farm.business || [];
        const list = Array.isArray(businesses) ? businesses : [businesses];
        if (!isset(farm.business_id) && list.filter(Boolean).length === 0) {
          return true;
        }
        return list.some(
          (business) => business && business.id == this.model.business_id
        );
      });
    },
  },
  methods: {
    addBed(index) {
      this.model.ids.splice(index + 1, 0, null);
      this.model.orders.splice(index + 1, 0, index + 2);
      this.model.names.splice(index + 1, 0, null);
    },
    removeBed(index) {
      ['ids', 'orders', 'names'].forEach((field) =>
        this.model[field].splice(index, 1)
      );
    },
    validate() {
      return this.$validator.validateAll().then((valid) => {
        if (!valid) return false;
        if (!isset(this.model.business_id)) {
          return this.validationInputsRequired('business');
        }
        if (!isset(this.model.farm_id)) {
          return this.validationInputsRequired('farm');
        }

        const business = this.businesses.find(
          (item) => item.id == this.model.business_id
        );
        const farm = this.farms.find((item) => item.id == this.model.farm_id);
        this.$emit('on-validated', {
          ...this.model,
          business_name: business ? business.name : null,
          farm_name: farm ? farm.name : null,
        });
        return true;
      });
    },
  },
  mounted() {
    Promise.all([
      Axios.getMasters('businesses/only'),
      Axios.getMasters('farms/farms/only'),
    ])
      .then(([businesses, farms]) => {
        this.businesses = businesses.data.data;
        this.farms = farms.data.data;
      })
      .catch((error) => this.showMessageErrors(error));

    if (this.isUpdate() && sessionStorage.getItem('editForm')) {
      const editForm = JSON.parse(sessionStorage.getItem('editForm'));
      const business = editForm.business || (editForm.businesses || [])[0];
      const names = Array.isArray(editForm.names)
        ? editForm.names
        : [editForm.name];
      const orders = Array.isArray(editForm.orders)
        ? editForm.orders
        : [editForm.order];
      const ids = Array.isArray(editForm.ids) ? editForm.ids : [editForm.id];
      this.model = {
        business_id: editForm.business_id || (business && business.id) || null,
        farm_id:
          editForm.farm_id || (editForm.farm && editForm.farm.id) || null,
        ids,
        orders,
        names,
      };
    }
  },
};
</script>
